import { Dialog, LinearProgress } from "@mui/material";
import { isEmpty } from "lodash-es";
import { type FC, useState } from "react";
import { useSupabaseLoader } from "../../../../../server/supabase/hooks";
import type { DialogController } from "../../../../../utils/hooks/use-dialog";
import { SupabaseSegmentService } from "../../../server/supabase-segment-service";
import type { SegmentCrudDialogState } from "../hooks/use-segment-crud-datagrid-dialog";
import { SegmentCrudDataGridDialogV2 } from "../segment-crud-data-grid-dialog-v2";
import type { SegmentRowWithOptions } from "../types";
import { SegmentModalForm } from "./segment-modal-form";
import { SegmentModalFormWrapper } from "./segment-modal-form-wrapper";
import {
  type EditModalTab,
  TabbedEditSegmentModal,
} from "./tabbed-edit-segment-modal/tabbed-edit-segment-modal";
import type { EditSegmentModalProps } from "./types";

export const EditSegmentModal: FC<
  EditSegmentModalProps & {
    v2Props: {
      dialog: DialogController<SegmentCrudDialogState>;
      reload: () => void;
      processedSegments: SegmentRowWithOptions[];
    };
  }
> = ({ v2Props, open, segmentId, onSave, onCancel, disabled, defaultTab }) => {
  const [tab, setTab] = useState<EditModalTab>(defaultTab ?? "DETAILS");

  const { data, loading } = useSupabaseLoader(
    async ({ supabase }) => {
      if (!segmentId) return;
      const resp = await new SupabaseSegmentService(supabase).getSegmentData(segmentId);

      if (resp.error) throw resp.error;

      return resp.data;
    },
    [segmentId],
  );

  if (!isEmpty(data?.segment_json))
    return (
      <SegmentCrudDataGridDialogV2
        is_recipient_type={data.is_recipient_type ?? false}
        {...v2Props}
      />
    );

  return (
    <Dialog open={open} fullWidth maxWidth="lg" onClose={onCancel}>
      {loading && <LinearProgress />}
      {data && (
        <SegmentModalFormWrapper
          onSave={onSave}
          onCancel={onCancel}
          initialValues={{ segment: data }}
          isRecipientType={data.is_recipient_type ?? false}
          showButtons={tab === "DETAILS"}
        >
          <TabbedEditSegmentModal segment={data} disabled={disabled} tab={tab} setTab={setTab}>
            <SegmentModalForm
              isRecipientType={data.is_recipient_type ?? false}
              disabled={disabled}
            />
          </TabbedEditSegmentModal>
        </SegmentModalFormWrapper>
      )}
    </Dialog>
  );
};
